import { z } from "zod";

// ==========================================
// Area Schema
// ==========================================
export const areaSchema = z.object({
    slug: z
        .string()
        .min(2, "Slug must be at least 2 characters")
        .regex(/^[a-z0-9-]+$/, "Must contain only lowercase letters, numbers, and hyphens"),
    image: z.string().optional().nullable(),
    order: z.coerce.number().optional(),
    active: z.boolean().optional(),
    nameEn: z.string().min(1, "English name is required"),
    nameAr: z.string().min(1, "Arabic name is required"),
    descriptionEn: z.string().optional(),
    descriptionAr: z.string().optional(),
});

// ==========================================
// Project Schema
// ==========================================
export const projectSchema = z.object({
    slug: z
        .string()
        .min(2, "Slug must be at least 2 characters")
        .regex(/^[a-z0-9-]+$/, "Must contain only lowercase letters, numbers, and hyphens"),
    price: z.coerce.number({ message: "Price must be a number" }).min(0, "Price must be positive"),
    image: z.string().min(1, "Main image is required"),
    areaId: z.string().min(1, "Area is required"),
    titleEn: z.string().min(1, "English title is required"),
    titleAr: z.string().min(1, "Arabic title is required"),
    descriptionEn: z.string().optional().nullable(),
    descriptionAr: z.string().optional().nullable(),
    areaRange: z.string().optional().nullable(),
    delivery: z.string().optional().nullable(),
    finish: z.string().optional().nullable(),
    paymentPlan: z.string().optional().nullable(),
    latitude: z.coerce.number().optional().nullable(),
    longitude: z.coerce.number().optional().nullable(),
    published: z.boolean().optional(),
    featured: z.boolean().optional(),
});

// ==========================================
// Unit Schema
// ==========================================
export const unitSchema = z.object({
    projectId: z.string().min(1, "Project is required"),
    slug: z
        .string()
        .min(2, "Slug must be at least 2 characters")
        .regex(/^[a-z0-9-]+$/, "Must contain only lowercase letters, numbers, and hyphens")
        .optional()
        .or(z.literal("")),
    price: z.coerce.number({ message: "Price must be a number" }).min(0, "Price must be positive"),
    beds: z.coerce.number().min(0, "Must be 0 or more").optional().nullable(),
    baths: z.coerce.number().min(0, "Must be 0 or more").optional().nullable(),
    area: z.coerce.number().min(1, "Area must be at least 1 m²").optional().nullable(),
    image: z.string().min(1, "Main image is required"),
    titleEn: z.string().min(1, "English title is required"),
    titleAr: z.string().min(1, "Arabic title is required"),
    status: z.string().optional(),
    constructionStatus: z.string().optional(),
    published: z.boolean().optional(),
    featured: z.boolean().optional(),
    categoryId: z.string().optional().nullable(),
    finishType: z.string().optional().nullable(),
    deliveryDate: z.string().optional().nullable(),
});

// ==========================================
// Amenity Schema
// ==========================================
export const amenitySchema = z.object({
    icon: z.string().min(1, "Icon is required"),
    nameEn: z.string().min(1, "English name is required"),
    nameAr: z.string().min(1, "Arabic name is required"),
});

// ==========================================
// Blog Post Schema (for quick-create forms)
// ==========================================
export const blogSchema = z.object({
    slug: z
        .string()
        .min(2, "Slug must be at least 2 characters")
        .regex(/^[a-z0-9-]+$/, "Must contain only lowercase letters, numbers, and hyphens"),
    titleEn: z.string().min(1, "English title is required"),
    titleAr: z.string().optional(),
    published: z.boolean().optional(),
});

// ==========================================
// User Schema
// ==========================================
export const userSchema = z.object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
    roleId: z.string().min(1, "Role is required"),
});

// ==========================================
// Role Schema
// ==========================================
export const roleSchema = z.object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    description: z.string().optional(),
});
